import React, { useEffect, useMemo, useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import Badge from '../components/ui/Badge';
import { useUser } from '../store/UserContext';
import { useStudentStore } from '../store/StudentStore';
import ExternalMessageService from '../services/ExternalMessageService';
import AssignmentService from '../services/AssignmentService';
import MessageService from '../services/MessageService';
import StaffService from '../services/StaffService';
import JournalService from '../services/JournalService';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const sortByDate = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

const buildFileMeta = (file) => ({
  name: file.name,
  type: file.type || 'file',
  size: `${Math.round(file.size / 1024)} KB`,
  fileUrl: ''
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({ ...buildFileMeta(file), fileUrl: reader.result?.toString() || '' });
    };
    reader.onerror = () => {
      resolve({ ...buildFileMeta(file), fileUrl: '' });
    };
    reader.readAsDataURL(file);
  });

const readFiles = async (fileList) => {
  const files = Array.from(fileList || []);
  if (files.length === 0) return [];
  return Promise.all(files.map((file) => readFileAsDataUrl(file)));
};

const getFileExtension = (file) => {
  if (!file?.name) return '';
  const parts = file.name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isZipFile = (file) => {
  const ext = getFileExtension(file);
  return ext === 'zip' || file.type === 'application/zip';
};

const getFileUrl = (file) => file?.fileUrl || file?.url || file?.link || '';

const openFileInNewWindow = (file) => {
  const url = getFileUrl(file);
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadFile = (file) => {
  const url = getFileUrl(file);
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name || 'download';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const confirmDelete = (label) => {
  const response = window.prompt(`Type YES to delete ${label}. This only removes it from SafeguardPro.`);
  return response && response.trim().toLowerCase() === 'yes';
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const buildSubmissionMap = (assignment) => {
  const map = new Map();
  (assignment.timeline || [])
    .filter((entry) => entry.type === 'submission' && entry.studentId)
    .forEach((entry) => {
      const existing = map.get(entry.studentId);
      if (!existing || new Date(entry.createdAt) > new Date(existing.createdAt)) {
        map.set(entry.studentId, entry);
      }
    });
  return map;
};

export default function ExternalMessagesPage() {
  const { user } = useUser();
  const { students } = useStudentStore();
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const assignmentService = useMemo(() => new AssignmentService(), []);
  const internalMessageService = useMemo(() => new MessageService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const journalService = useMemo(() => new JournalService(), []);

  const [messages, setMessages] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activeView, setActiveView] = useState('messages');
  const [activeKpi, setActiveKpi] = useState(null);
  const [activeMessageTab, setActiveMessageTab] = useState('inbox');
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [activeAssignmentId, setActiveAssignmentId] = useState(null);
  const [assignmentTab, setAssignmentTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [messageCategory, setMessageCategory] = useState('update');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState('');

  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyStudentId, setReplyStudentId] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');

  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentDraft, setAssignmentDraft] = useState({
    title: '',
    description: '',
    dueDate: '',
    studentIds: [],
    attachments: []
  });
  const [assignmentStatus, setAssignmentStatus] = useState('');

  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [submissionStudentId, setSubmissionStudentId] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);

  const [commentDraft, setCommentDraft] = useState('');
  const [reviewDrafts, setReviewDrafts] = useState({});

  useEffect(() => {
    if (!user) return;
    setMessages(messageService.listForStaff(user.id));
  }, [messageService, user]);

  useEffect(() => {
    const visibleStudentIds = new Set(students.map((student) => student.id));
    const visibleAssignments = assignmentService.listForStudents([...visibleStudentIds]);
    setAssignments(visibleAssignments);
  }, [assignmentService, students]);

  const studentMap = new Map(students.map((student) => [student.id, student]));
  const visibleStudents = students;

  const inboxMessages = messages.filter(
    (message) => message.fromType === 'student' && message.staffRecipients.includes(user.id)
  ).sort(sortByDate);
  const sentMessages = messages.filter(
    (message) => message.fromType === 'staff' && message.fromId === user.id
  ).sort(sortByDate);
  const unreadCount = inboxMessages.filter((message) => !message.readBy.includes(user.id)).length;

  const assignmentStats = useMemo(() => {
    const now = new Date();
    const dueSoonThreshold = new Date();
    dueSoonThreshold.setDate(now.getDate() + 7);
    const active = assignments.filter((assignment) => assignment.status !== 'archived');
    const overdue = active.filter((assignment) => {
      const dueDate = parseDateValue(assignment.dueDate);
      return dueDate && dueDate < now;
    });
    const dueSoon = active.filter((assignment) => {
      const dueDate = parseDateValue(assignment.dueDate);
      return dueDate && dueDate >= now && dueDate <= dueSoonThreshold;
    });
    return { active, overdue, dueSoon };
  }, [assignments]);

  const kpiItems = [
    { id: 'messages', label: 'Student messages', value: inboxMessages.length },
    { id: 'unread', label: 'Unread from students', value: unreadCount, badge: { label: 'New', variant: 'danger' } },
    { id: 'assignments', label: 'Active assignments', value: assignmentStats.active.length },
    { id: 'overdue', label: 'Overdue submissions', value: assignmentStats.overdue.length, badge: { label: 'Overdue', variant: 'danger' } }
  ];

  const activeMessages = activeMessageTab === 'sent' ? sentMessages : inboxMessages;
  const filteredMessages = activeMessages.filter((message) => {
    if (activeKpi === 'unread' && message.fromType === 'student') {
      return !message.readBy.includes(user.id);
    }
    const haystack = `${message.subject} ${message.body} ${message.fromName} ${(message.recipients || [])
      .map((rec) => rec.name)
      .join(' ')}`.toLowerCase();
    return haystack.includes(searchQuery.toLowerCase());
  });

  const getLatestReviewMap = (assignment) => {
    const map = new Map();
    if (!assignment) return map;
    assignment.timeline
      .filter((entry) => entry.type === 'review' && entry.studentId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach((entry) => {
        if (!map.has(entry.studentId)) {
          map.set(entry.studentId, entry);
        }
      });
    return map;
  };

  const isAssignmentCompleted = (assignment) => {
    if (!assignment) return false;
    const reviewMap = getLatestReviewMap(assignment);
    if (!assignment.studentIds.length) return false;
    return assignment.studentIds.every((studentId) => {
      const review = reviewMap.get(studentId);
      return review?.reviewStatus === 'approved';
    });
  };

  const activeAssignments = assignments.filter((assignment) => !isAssignmentCompleted(assignment));
  const completedAssignments = assignments.filter((assignment) => isAssignmentCompleted(assignment));

  const activeMessage = filteredMessages.find((message) => message.id === activeMessageId) ||
    activeMessages.find((message) => message.id === activeMessageId);

  const visibleAssignments = assignmentTab === 'completed' ? completedAssignments : activeAssignments;
  const activeAssignment =
    visibleAssignments.find((assignment) => assignment.id === activeAssignmentId) ||
    assignments.find((assignment) => assignment.id === activeAssignmentId) ||
    null;
  const activeSubmissionMap = activeAssignment ? buildSubmissionMap(activeAssignment) : new Map();
  const activeReviewMap = useMemo(() => {
    if (!activeAssignment) return new Map();
    const map = new Map();
    activeAssignment.timeline
      .filter((entry) => entry.type === 'review' && entry.studentId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach((entry) => {
        if (!map.has(entry.studentId)) {
          map.set(entry.studentId, entry);
        }
      });
    return map;
  }, [activeAssignment]);

  if (!user) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Sign in to view external messages.</p>
      </Card>
    );
  }

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]));
  };

  const toggleAssignmentStudent = (id) => {
    setAssignmentDraft((prev) => ({
      ...prev,
      studentIds: prev.studentIds.includes(id)
        ? prev.studentIds.filter((entry) => entry !== id)
        : [...prev.studentIds, id]
    }));
  };

  const handleSelectMessage = (message) => {
    setActiveMessageId(message.id);
    if (message.fromType === 'student' && !message.readBy.includes(user.id)) {
      messageService.markRead(message.id, user.id);
      setMessages(messageService.listForStaff(user.id));
    }
  };

  const handleSendMessage = () => {
    setStatus('');
    if (!subject.trim()) {
      setStatus('Add a subject.');
      return;
    }
    if (selectedStudentIds.length === 0) {
      setStatus('Select at least one student.');
      return;
    }
    const recipients = selectedStudentIds
      .map((id) => studentMap.get(id))
      .filter(Boolean)
      .map((student) => ({ id: student.id, name: student.name, type: 'student' }));
    const createdEntries = recipients.map((recipient) =>
      journalService.createEntry({
        student: { id: recipient.id, name: recipient.name },
        title: subject.trim(),
        body: body.trim(),
        category: messageCategory,
        attachments,
        staffRecipients: [{ id: user.id, name: user.name }],
        authorType: 'staff',
        authorName: user.name,
        authorId: user.id
      })
    );
    messageService.sendFromStaff({
      subject: subject.trim(),
      body: body.trim(),
      fromUser: user,
      recipients,
      attachments,
      category: messageCategory,
      references: createdEntries.map((entry) => ({
        type: 'journal',
        id: entry.id,
        label: `${entry.studentName} journal entry`
      }))
    });
    setMessages(messageService.listForStaff(user.id));
    setComposeOpen(false);
    setSubject('');
    setBody('');
    setSelectedStudentIds([]);
    setAttachments([]);
    setStatus('');
    setActiveMessageTab('sent');
  };

  const handleLogStudentReply = () => {
    if (!replyStudentId || !replySubject.trim()) return;
    const student = studentMap.get(replyStudentId);
    if (!student) return;
    messageService.logStudentMessage({
      subject: replySubject.trim(),
      body: replyBody.trim(),
      student,
      staffRecipients: [{ id: user.id, name: user.name }]
    });
    setMessages(messageService.listForStaff(user.id));
    setReplyModalOpen(false);
    setReplyStudentId('');
    setReplySubject('');
    setReplyBody('');
  };

  const handleCreateAssignment = () => {
    setAssignmentStatus('');
    if (!assignmentDraft.title.trim()) {
      setAssignmentStatus('Add a title.');
      return;
    }
    if (assignmentDraft.studentIds.length === 0) {
      setAssignmentStatus('Select at least one student.');
      return;
    }
    const assignment = assignmentService.create({
      title: assignmentDraft.title.trim(),
      description: assignmentDraft.description.trim(),
      dueDate: assignmentDraft.dueDate,
      createdBy: user.name,
      createdById: user.id,
      studentIds: assignmentDraft.studentIds,
      attachments: assignmentDraft.attachments
    });
    const recipients = assignmentDraft.studentIds
      .map((id) => studentMap.get(id))
      .filter(Boolean)
      .map((student) => ({ id: student.id, name: student.name, type: 'student' }));
    messageService.sendFromStaff({
      subject: `New assignment: ${assignment.title}`,
      body: assignment.description || 'A new assignment has been created.',
      fromUser: user,
      recipients,
      attachments: assignment.attachments,
      category: 'assignment',
      references: [{ type: 'assignment', id: assignment.id, label: assignment.title }]
    });
    setAssignments(assignmentService.listForStudents(students.map((s) => s.id)));
    setAssignmentModalOpen(false);
    setAssignmentDraft({ title: '', description: '', dueDate: '', studentIds: [], attachments: [] });
    setActiveView('assignments');
    setActiveAssignmentId(assignment.id);
  };

  const handleAddComment = () => {
    if (!activeAssignment || !commentDraft.trim()) return;
    assignmentService.addTimelineEntry(activeAssignment.id, {
      type: 'comment',
      message: commentDraft.trim(),
      createdBy: user.name
    });
    setAssignments(assignmentService.listForStudents(students.map((s) => s.id)));
    const recipients = activeAssignment.studentIds
      .map((id) => studentMap.get(id))
      .filter(Boolean)
      .map((student) => ({ id: student.id, name: student.name, type: 'student' }));
    messageService.sendFromStaff({
      subject: `Update on assignment: ${activeAssignment.title}`,
      body: commentDraft.trim(),
      fromUser: user,
      recipients,
      category: 'assignment'
    });
    setMessages(messageService.listForStaff(user.id));
    setCommentDraft('');
  };

  const refreshAssignments = () => {
    const visibleStudentIds = students.map((student) => student.id);
    setAssignments(assignmentService.listForStudents(visibleStudentIds));
  };

  const handleDeleteAssignmentAttachment = (index) => {
    if (!activeAssignment) return;
    const file = activeAssignment.attachments?.[index];
    if (!file) return;
    if (!confirmDelete(file.name || 'this file')) return;
    const nextAttachments = activeAssignment.attachments.filter((_, idx) => idx !== index);
    assignmentService.update(activeAssignment.id, { attachments: nextAttachments });
    refreshAssignments();
  };

  const handleDeleteTimelineAttachment = (entryId, index) => {
    if (!activeAssignment) return;
    const entry = activeAssignment.timeline.find((item) => item.id === entryId);
    if (!entry) return;
    const file = entry.attachments?.[index];
    if (!file) return;
    if (!confirmDelete(file.name || 'this file')) return;
    const nextTimeline = activeAssignment.timeline.map((item) => {
      if (item.id !== entryId) return item;
      return {
        ...item,
        attachments: (item.attachments || []).filter((_, idx) => idx !== index)
      };
    });
    assignmentService.update(activeAssignment.id, { timeline: nextTimeline });
    refreshAssignments();
  };

  const handleLogSubmission = () => {
    if (!activeAssignment || !submissionStudentId) return;
    const student = studentMap.get(submissionStudentId);
    if (!student) return;
    assignmentService.addTimelineEntry(activeAssignment.id, {
      type: 'submission',
      message: submissionNotes || 'Submission uploaded.',
      createdBy: student.name,
      studentId: student.id,
      attachments: submissionFiles
    });
    setAssignments(assignmentService.listForStudents(students.map((s) => s.id)));
    messageService.sendFromStaff({
      subject: `Submission received: ${activeAssignment.title}`,
      body: `We received ${student.name}'s submission.`,
      fromUser: user,
      recipients: [{ id: student.id, name: student.name, type: 'student' }],
      category: 'assignment'
    });
    if (activeAssignment.createdById && activeAssignment.createdById !== user.id) {
      const staff = staffService.list().find((member) => member.id === activeAssignment.createdById);
      if (staff) {
        internalMessageService.send({
          subject: `Assignment submission: ${activeAssignment.title}`,
          body: `${student.name} submitted files for ${activeAssignment.title}.`,
          fromUser: user,
          recipients: [{ id: staff.id, name: staff.name, type: 'staff' }],
          references: []
        });
      }
    }
    setMessages(messageService.listForStaff(user.id));
    setSubmissionOpen(false);
    setSubmissionStudentId('');
    setSubmissionNotes('');
    setSubmissionFiles([]);
  };

  const handleReviewDraftChange = (studentId, updates) => {
    setReviewDrafts((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), ...updates }
    }));
  };

  const handleLogReview = (studentId) => {
    if (!activeAssignment || !studentId) return;
    const draft = reviewDrafts[studentId] || {};
    const status = draft.status || 'approved';
    if (status === 'resubmit' && !draft.dueDate) {
      window.alert('Please add a resubmission due date.');
      return;
    }
    assignmentService.addTimelineEntry(activeAssignment.id, {
      type: 'review',
      message: draft.notes || `${status} submission`,
      createdBy: user.name,
      studentId,
      reviewStatus: status,
      reviewDueDate: draft.dueDate || ''
    });
    const updated = assignmentService.getById(activeAssignment.id);
    if (updated && isAssignmentCompleted(updated)) {
      assignmentService.update(updated.id, { status: 'completed' });
    }
    refreshAssignments();
    setReviewDrafts((prev) => ({ ...prev, [studentId]: { status: '', dueDate: '', notes: '' } }));
  };

  const renderAttachmentRow = (file, index, onDelete) => {
    const zipOnly = isZipFile(file);
    const url = getFileUrl(file);
    const canPreview = Boolean(url) && !zipOnly;
    return (
      <div key={`${file.name}-${index}`} className="sp-panel sp-panel--muted p-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm">{file.name}</div>
          <div className="flex items-center gap-2">
            {canPreview && (
              <Button size="sm" variant="secondary" type="button" onClick={() => openFileInNewWindow(file)}>
                View
              </Button>
            )}
            <Button size="sm" variant="ghost" type="button" onClick={() => downloadFile(file)} disabled={!url}>
              Download
            </Button>
            {onDelete && (
              <Button size="sm" variant="ghost" type="button" onClick={onDelete}>
                Delete
              </Button>
            )}
          </div>
        </div>
        {zipOnly && <div className="text-xs sp-muted mt-1">Zip file - download only.</div>}
        {!url && <div className="text-xs sp-muted mt-1">No file data available.</div>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="sp-page-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>External Messages</h2>
          <p className="sp-muted">Student-facing communication, class notes, and assignment workflows.</p>
        </div>
        <div className="sp-page-actions">
          <Button size="sm" variant="secondary" onClick={() => setReplyModalOpen(true)}>
            Log student reply
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setAssignmentModalOpen(true)}>
            New assignment
          </Button>
          <Button size="sm" onClick={() => setComposeOpen((prev) => !prev)}>
            {composeOpen ? 'Close message' : 'New message'}
          </Button>
        </div>
      </div>

      <WorkspaceKpis
        items={kpiItems}
        activeKpi={activeKpi}
        onSelect={(kpiId) => {
          setActiveKpi(kpiId);
          if (kpiId === 'assignments' || kpiId === 'overdue') {
            setActiveView('assignments');
          } else {
            setActiveView('messages');
            setActiveMessageTab('inbox');
          }
        }}
      />

      {composeOpen && (
        <Card className="p-6 space-y-4">
          <div className="sp-panel sp-panel--muted p-3 text-xs">
            Messages are delivered to students and saved to their Journal as support team updates.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="sp-label">Students</label>
                <SearchableChecklist
                  items={visibleStudents}
                  selectedIds={selectedStudentIds}
                  onToggle={toggleStudent}
                  getId={(student) => student.id}
                  getLabel={(student) => student.name}
                  getMeta={(student) => student.reference || 'Student'}
                  placeholder="Search students"
                  emptyLabel="No students available"
                />
              </div>
              {selectedStudentIds.length > 0 && (
                <div className="text-xs sp-muted">
                  Sending to {selectedStudentIds.length} student{selectedStudentIds.length === 1 ? '' : 's'}.
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <label className="sp-label">Message type</label>
                <select
                  className="sp-select mt-2 w-full"
                  value={messageCategory}
                  onChange={(event) => setMessageCategory(event.target.value)}
                >
                  <option value="update">Update</option>
                  <option value="class-notes">Class notes</option>
                  <option value="attendance">Attendance summary</option>
                  <option value="report">Report summary</option>
                  <option value="assignment">Assignment update</option>
                </select>
                <div className="text-xs sp-muted mt-1">Journal entry will use this category.</div>
              </div>
              <div>
                <label className="sp-label">Subject</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </div>
              <div>
                <label className="sp-label">Message</label>
                <textarea
                  className="sp-textarea mt-2 w-full"
                  rows={6}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
              </div>
              <div>
                <label className="sp-label">Attachments</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={async (event) => {
                    const files = await readFiles(event.target.files);
                    setAttachments(files);
                  }}
                />
                {attachments.length > 0 && (
                  <div className="text-xs sp-muted mt-2">
                    {attachments.map((file) => file.name).join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
          {status && <div className="text-xs sp-warning-text">{status}</div>}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSendMessage}>Send message</Button>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={activeView === 'messages' ? 'secondary' : 'ghost'}
                  onClick={() => {
                    setActiveView('messages');
                    setActiveKpi(null);
                  }}
                >
                  Messages
                </Button>
                <Button
                  size="sm"
                  variant={activeView === 'assignments' ? 'secondary' : 'ghost'}
                  onClick={() => {
                    setActiveView('assignments');
                    setActiveKpi(null);
                  }}
                >
                  Assignments
                </Button>
              </div>
              {activeView === 'messages' && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={activeMessageTab === 'inbox' ? 'secondary' : 'ghost'}
                    onClick={() => setActiveMessageTab('inbox')}
                  >
                    Inbox
                  </Button>
                  <Button
                    size="sm"
                    variant={activeMessageTab === 'sent' ? 'secondary' : 'ghost'}
                    onClick={() => setActiveMessageTab('sent')}
                  >
                    Sent
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="sp-label">
                {activeView === 'assignments' ? 'Assignment list' : 'Student messages'}
              </div>
              <input
                className="sp-input w-full md:w-64"
                placeholder={activeView === 'assignments' ? 'Search assignments' : 'Search messages'}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            {activeView === 'messages' ? (
              <div className="space-y-2">
                {filteredMessages.length === 0 && (
                  <div className="sp-panel sp-panel--muted p-3 text-sm">No messages found.</div>
                )}
                {filteredMessages.map((message) => (
                  <button
                    key={message.id}
                    type="button"
                    className={`sp-panel w-full p-3 text-left ${activeMessageId === message.id ? '' : 'sp-panel--muted'}`}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="sp-value" style={{ fontWeight: 600 }}>{message.subject}</div>
                      <div className="text-xs sp-muted">{formatDateTime(message.createdAt)}</div>
                    </div>
                    <div className="text-xs sp-muted">
                      {message.fromType === 'student' ? `From: ${message.fromName}` : `To: ${(message.recipients || [])
                        .map((rec) => rec.name)
                        .join(', ')}`}
                    </div>
                    {message.category && (
                      <div className="text-xs sp-muted mt-1">Type: {message.category.replace('-', ' ')}</div>
                    )}
                    {!message.readBy.includes(user.id) && message.fromType === 'student' && (
                      <div className="text-xs sp-warning-text mt-1">Unread</div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={assignmentTab === 'active' ? 'secondary' : 'ghost'}
                    onClick={() => setAssignmentTab('active')}
                  >
                    Active
                  </Button>
                  <Button
                    size="sm"
                    variant={assignmentTab === 'completed' ? 'secondary' : 'ghost'}
                    onClick={() => setAssignmentTab('completed')}
                  >
                    Completed
                  </Button>
                </div>
                {visibleAssignments.length === 0 && (
                  <div className="sp-panel sp-panel--muted p-3 text-sm">No assignments created yet.</div>
                )}
                {visibleAssignments
                  .filter((assignment) => {
                    if (activeKpi === 'overdue') {
                      const dueDate = parseDateValue(assignment.dueDate);
                      if (!dueDate || dueDate >= new Date()) return false;
                    }
                    return assignment.title.toLowerCase().includes(searchQuery.toLowerCase());
                  })
                  .map((assignment) => {
                    const dueDate = parseDateValue(assignment.dueDate);
                    const isOverdue = dueDate && dueDate < new Date();
                    const submissionMap = buildSubmissionMap(assignment);
                    const submittedCount = submissionMap.size;
                    const totalCount = assignment.studentIds.length;
                    return (
                      <button
                        key={assignment.id}
                        type="button"
                        className={`sp-panel w-full p-3 text-left ${activeAssignmentId === assignment.id ? '' : 'sp-panel--muted'} ${
                          isOverdue ? 'sp-panel--critical' : ''
                        }`}
                        onClick={() => {
                          setActiveAssignmentId(assignment.id);
                          setActiveView('assignments');
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="sp-value" style={{ fontWeight: 600 }}>{assignment.title}</div>
                          <div className="text-xs sp-muted">{assignment.dueDate ? formatDate(assignment.dueDate) : 'No due date'}</div>
                        </div>
                        <div className="text-xs sp-muted">
                          {assignment.studentIds.length} student{assignment.studentIds.length === 1 ? '' : 's'} ·
                          Submissions {submittedCount}/{totalCount}
                        </div>
                        {isOverdue && <div className="text-xs sp-warning-text mt-1">Overdue submissions</div>}
                      </button>
                    );
                  })}
              </div>
            )}
          </Card>
        </div>

        <Card className="p-4 space-y-3">
          {activeView === 'messages' ? (
            !activeMessage ? (
              <div className="text-sm sp-muted">Select a message to view details.</div>
            ) : (
              <>
                <div>
                  <div className="sp-value" style={{ fontWeight: 600 }}>{activeMessage.subject}</div>
                  <div className="text-xs sp-muted">
                    {activeMessage.fromType === 'student'
                      ? `From: ${activeMessage.fromName}`
                      : `To: ${(activeMessage.recipients || []).map((rec) => rec.name).join(', ')}`} · {formatDateTime(activeMessage.createdAt)}
                  </div>
                </div>
                <div className="text-sm whitespace-pre-wrap">{activeMessage.body || 'No message body.'}</div>
                {activeMessage.references?.some((ref) => ref.type === 'journal') && (
                  <div className="sp-panel sp-panel--muted p-3 text-xs">
                    <div className="sp-label">Journal link</div>
                    {activeMessage.references
                      .filter((ref) => ref.type === 'journal')
                      .map((ref) => (
                        <div key={ref.id}>{ref.label || 'Saved to journal'}</div>
                      ))}
                  </div>
                )}
                {activeMessage.attachments?.length > 0 && (
                  <div className="sp-panel sp-panel--muted p-3 text-xs space-y-2">
                    <div className="sp-label">Attachments</div>
                    {activeMessage.attachments.map((file, idx) =>
                      renderAttachmentRow(file, idx, null)
                    )}
                  </div>
                )}
              </>
            )
          ) : (
            !activeAssignment ? (
              <div className="text-sm sp-muted">Select an assignment to view details.</div>
            ) : (
              <>
                {(() => {
                  const submittedCount = activeSubmissionMap.size;
                  const totalCount = activeAssignment.studentIds.length;
                  return (
                    <div className="flex items-center gap-2 text-xs sp-muted">
                      <span>Submissions {submittedCount}/{totalCount}</span>
                      {submittedCount < totalCount && (
                        <span className="sp-warning-text">Outstanding {totalCount - submittedCount}</span>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <div className="sp-value" style={{ fontWeight: 600 }}>{activeAssignment.title}</div>
                  <div className="text-xs sp-muted">
                    Due {activeAssignment.dueDate ? formatDate(activeAssignment.dueDate) : 'Not set'} · Created by {activeAssignment.createdBy}
                  </div>
                </div>
                <div className="text-sm">{activeAssignment.description || 'No description provided.'}</div>
                {activeAssignment.attachments?.length > 0 && (
                  <div className="sp-panel sp-panel--muted p-3 text-xs space-y-2">
                    <div className="sp-label">Attachments</div>
                    {activeAssignment.attachments.map((file, idx) =>
                      renderAttachmentRow(file, idx, () => handleDeleteAssignmentAttachment(idx))
                    )}
                  </div>
                )}
                <div>
                  <div className="sp-label">Submission status</div>
                  <div className="space-y-2 text-sm sp-muted">
                    {activeAssignment.studentIds.map((studentId) => {
                      const entry = activeSubmissionMap.get(studentId);
                      const review = activeReviewMap.get(studentId);
                      const student = studentMap.get(studentId);
                      const draft = reviewDrafts[studentId] || {};
                      const statusLabel = review?.reviewStatus || (entry ? 'submitted' : 'pending');
                      return (
                        <div key={studentId} className="sp-panel sp-panel--muted p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div>
                              <div className="sp-value" style={{ fontWeight: 600 }}>{student?.name || studentId}</div>
                              <div className="text-xs sp-muted">
                                {entry ? `Submitted ${formatDateTime(entry.createdAt)}` : 'No submission yet'}
                              </div>
                            </div>
                            <Badge variant={statusLabel === 'approved' ? 'success' : statusLabel === 'resubmit' ? 'review' : statusLabel === 'failed' ? 'danger' : 'inactive'}>
                              {statusLabel === 'submitted' ? 'Submitted' : statusLabel}
                            </Badge>
                          </div>
                          {entry?.attachments?.length > 0 && (
                            <div className="space-y-2">
                              {entry.attachments.map((file, idx) =>
                                renderAttachmentRow(file, idx, null)
                              )}
                            </div>
                          )}
                          <div className="grid md:grid-cols-3 gap-2">
                            <div>
                              <label className="sp-label">Review status</label>
                              <select
                                className="sp-select mt-1 w-full"
                                value={draft.status || ''}
                                onChange={(event) =>
                                  handleReviewDraftChange(studentId, { status: event.target.value })
                                }
                              >
                                <option value="">Choose</option>
                                <option value="approved">Approved</option>
                                <option value="resubmit">Resubmit</option>
                                <option value="failed">Failed</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            <div>
                              <label className="sp-label">Resubmit due</label>
                              <DateSelect
                                value={draft.dueDate || ''}
                                onChange={(value) => handleReviewDraftChange(studentId, { dueDate: value })}
                                className="mt-1 w-full"
                              />
                            </div>
                            <div>
                              <label className="sp-label">Notes</label>
                              <input
                                className="sp-input mt-1 w-full"
                                value={draft.notes || ''}
                                onChange={(event) =>
                                  handleReviewDraftChange(studentId, { notes: event.target.value })
                                }
                                placeholder="Optional"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-end">
                            <Button size="sm" variant="secondary" onClick={() => handleLogReview(studentId)}>
                              Save review
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="sp-label">Student list</div>
                  <div className="text-sm sp-muted">
                    {activeAssignment.studentIds
                      .map((id) => studentMap.get(id)?.name)
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
                <div className="sp-panel sp-panel--muted p-3 space-y-2">
                  <div className="sp-label">Add comment</div>
                  <textarea
                    className="sp-textarea w-full"
                    rows={3}
                    value={commentDraft}
                    onChange={(event) => setCommentDraft(event.target.value)}
                  />
                  <Button size="sm" variant="secondary" onClick={handleAddComment} disabled={!commentDraft.trim()}>
                    Post comment
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => setSubmissionOpen(true)}>
                    Log submission
                  </Button>
                </div>
                <div>
                  <div className="sp-label">Timeline</div>
                  <div className="space-y-2 mt-2">
                    {activeAssignment.timeline
                      .slice()
                      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                      .map((entry) => (
                        <div key={entry.id} className="sp-panel sp-panel--muted p-3 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <div className="sp-value" style={{ fontWeight: 600 }}>
                              {entry.type === 'submission' ? 'Submission' : entry.type === 'comment' ? 'Comment' : 'Update'}
                            </div>
                            <div className="text-xs sp-muted">{formatDateTime(entry.createdAt)}</div>
                          </div>
                          <div className="text-xs sp-muted">By {entry.createdBy}</div>
                          {entry.message && <div className="mt-2">{entry.message}</div>}
                          {entry.attachments?.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {entry.attachments.map((file, idx) =>
                                renderAttachmentRow(file, idx, () => handleDeleteTimelineAttachment(entry.id, idx))
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </>
            )
          )}
        </Card>
      </div>

      <Modal isOpen={replyModalOpen} onClose={() => setReplyModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Log student reply</h3>
              <p className="text-sm sp-muted">Capture a student message received outside the platform.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setReplyModalOpen(false)}>Close</Button>
          </div>
          <div>
            <label className="sp-label">Student</label>
            <select
              className="sp-select mt-2 w-full"
              value={replyStudentId}
              onChange={(event) => setReplyStudentId(event.target.value)}
            >
              <option value="">Select student</option>
              {visibleStudents.map((student) => (
                <option key={student.id} value={student.id}>{student.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="sp-label">Subject</label>
            <input
              className="sp-input mt-2 w-full"
              value={replySubject}
              onChange={(event) => setReplySubject(event.target.value)}
            />
          </div>
          <div>
            <label className="sp-label">Message</label>
            <textarea
              className="sp-textarea mt-2 w-full"
              rows={4}
              value={replyBody}
              onChange={(event) => setReplyBody(event.target.value)}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setReplyModalOpen(false)}>Cancel</Button>
            <Button onClick={handleLogStudentReply} disabled={!replyStudentId || !replySubject.trim()}>
              Log reply
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={assignmentModalOpen} onClose={() => setAssignmentModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>New assignment</h3>
              <p className="text-sm sp-muted">Create an assignment with uploads and a timeline.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAssignmentModalOpen(false)}>Close</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="sp-label">Title</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={assignmentDraft.title}
                  onChange={(event) => setAssignmentDraft((prev) => ({ ...prev, title: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Due date</label>
                <DateSelect
                  value={assignmentDraft.dueDate}
                  onChange={(value) => setAssignmentDraft((prev) => ({ ...prev, dueDate: value }))}
                  className="mt-2 w-full"
                />
              </div>
              <div>
                <label className="sp-label">Attachments</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={async (event) => {
                    const files = await readFiles(event.target.files);
                    setAssignmentDraft((prev) => ({ ...prev, attachments: files }));
                  }}
                />
                {assignmentDraft.attachments.length > 0 && (
                  <div className="text-xs sp-muted mt-2">
                    {assignmentDraft.attachments.map((file) => file.name).join(', ')}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="sp-label">Description</label>
                <textarea
                  className="sp-textarea mt-2 w-full"
                  rows={4}
                  value={assignmentDraft.description}
                  onChange={(event) => setAssignmentDraft((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Students</label>
                <SearchableChecklist
                  items={visibleStudents}
                  selectedIds={assignmentDraft.studentIds}
                  onToggle={toggleAssignmentStudent}
                  getId={(student) => student.id}
                  getLabel={(student) => student.name}
                  getMeta={(student) => student.reference || 'Student'}
                  placeholder="Search students"
                  emptyLabel="No students available"
                />
              </div>
            </div>
          </div>
          {assignmentStatus && <div className="text-xs sp-warning-text">{assignmentStatus}</div>}
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setAssignmentModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateAssignment}>Create assignment</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submissionOpen} onClose={() => setSubmissionOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Log submission</h3>
              <p className="text-sm sp-muted">Capture a student upload (demo until student login is live).</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSubmissionOpen(false)}>Close</Button>
          </div>
          <div>
            <label className="sp-label">Student</label>
            <select
              className="sp-select mt-2 w-full"
              value={submissionStudentId}
              onChange={(event) => setSubmissionStudentId(event.target.value)}
            >
              <option value="">Select student</option>
              {activeAssignment?.studentIds.map((id) => {
                const student = studentMap.get(id);
                if (!student) return null;
                return (
                  <option key={student.id} value={student.id}>{student.name}</option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="sp-label">Notes</label>
            <textarea
              className="sp-textarea mt-2 w-full"
              rows={3}
              value={submissionNotes}
              onChange={(event) => setSubmissionNotes(event.target.value)}
            />
          </div>
          <div>
            <label className="sp-label">Files</label>
            <input
              type="file"
              multiple
              className="sp-input mt-2 w-full"
              onChange={async (event) => {
                const files = await readFiles(event.target.files);
                setSubmissionFiles(files);
              }}
            />
            {submissionFiles.length > 0 && (
              <div className="text-xs sp-muted mt-2">
                {submissionFiles.map((file) => file.name).join(', ')}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setSubmissionOpen(false)}>Cancel</Button>
            <Button onClick={handleLogSubmission} disabled={!submissionStudentId}>
              Log submission
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
